import { p as proxyCustomElement, H, c as createEvent, h, F as Fragment, a as Host } from './index.js';
import { t as toValue, a as toLabel } from './p-CPuegN2k.js';
import { g as getFilterOptionsFn, h as handleOptions, i as isOtherOption } from './p-D36toVjT.js';
import { d as defineCustomElement$c, H as HiddenHTMLDataList } from './p-BY8GADaY.js';
import { d as defineCustomElement$i } from './p-BbTg3Car.js';
import { d as defineCustomElement$h } from './p-DzNXepTc.js';
import { d as defineCustomElement$g } from './p-B7ipNcUy.js';
import { d as defineCustomElement$f } from './p-C9_XXP_a.js';
import { d as defineCustomElement$e } from './p-Dpb0Xahb.js';
import { d as defineCustomElement$d } from './p-Da2lAZSv.js';
import { d as defineCustomElement$b } from './p-DUcqAgyP.js';
import { d as defineCustomElement$a } from './p-CyejrxOi.js';
import { d as defineCustomElement$9 } from './p-ngrwPeVm.js';
import { d as defineCustomElement$8 } from './p-BU1hvS3W.js';
import { d as defineCustomElement$7 } from './p-DjGiWBv3.js';
import { d as defineCustomElement$6 } from './p-BtEI0Sh2.js';
import { d as defineCustomElement$5 } from './p-CT2oAZeh.js';
import { d as defineCustomElement$4 } from './p-CssqlikZ.js';
import { d as defineCustomElement$3 } from './p-CFAyJ2yW.js';
import { d as defineCustomElement$2 } from './p-ClSUnmWE.js';
import { d as debounce } from './p-Dg7s1ivP.js';

const autocompleteCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;position:relative;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}";

const WuiAutocomplete$1 = /*@__PURE__*/ proxyCustomElement(class WuiAutocomplete extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.choose = createEvent(this, "choose", 3);
        this.searchable = true;
        /**
         * Placeholder (displayed when `value` is undefined)
         */
        this.placeholder = '';
        /**
         * Disable component
         */
        this.disabled = false;
        /**
         * Show loading spinner
         *
         * Note: the loading state is automatically enabled when using async options
         */
        this.loading = false;
        /**
         * Display 'x' clear button when true
         */
        this.canClear = true;
        /**
         * Message to show when there are no search results
         *
         * Defaults to "No results"
         */
        this.noResultsMessage = 'No results';
        /**
         * If menu is open
         */
        this.open = false;
        this.searchQuery = '';
        this.abortControllers = [];
        this.filterOptions = getFilterOptionsFn(this);
        this.filterOptionsDebounced = debounce(this.filterOptions, 250);
    }
    async watchOptions(newValue) {
        return handleOptions(this, newValue);
    }
    focus() {
        this.wuiInputElm?.focus();
    }
    async handleKeyDown(e) {
        // Handle keyboard navigating through MenuPane
        const wuiMenuPaneElement = this.elm.shadowRoot?.querySelector('wui-menu-pane');
        if (wuiMenuPaneElement && (await wuiMenuPaneElement.handleKeyDown(e))) {
            // Event was handled by menu pane
            this.open = !!wuiMenuPaneElement.open;
            return;
        }
        const wuiListElement = this.elm.shadowRoot?.querySelector('wui-list');
        if (wuiListElement && (await wuiListElement.handleKeyDown(e))) {
            return;
        }
        // If the list did not handle the 'Enter' key, then it means there
        // was no option to select. In that case we'll emit the `choose`
        // event with the current value as a non-existing option.
        if (e.key === 'Enter') {
            // Emit the input's current value
            this.chooseValue(this.value, false);
        }
    }
    componentWillLoad() {
        const old = {
            loading: this.loading,
            disabled: this.disabled,
        };
        this.loading = true;
        this.disabled = true;
        // Trigger options validation for initial value
        this.watchOptions(this.options).then(() => {
            this.loading = old.loading;
            this.disabled = old.disabled;
        });
    }
    toggleMenuPane(open = !this.open) {
        if (this.disabled || this.open === open) {
            return;
        }
        this.open = open;
    }
    setValue(newValue) {
        this.value = newValue;
        this.searchQuery = newValue; // Equal to value
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
    }
    chooseValue(value, exists) {
        // Emit the select event with the chosen option's value
        const event = this.choose.emit({ value, exists });
        if (!event.defaultPrevented) {
            // Clear input
            this.setValue('');
            // Close menu pane
            this.toggleMenuPane(false);
        }
    }
    handleSelect(option) {
        const newValue = toValue(option ?? '');
        this.chooseValue(newValue, true);
    }
    handleQuery(e) {
        const { value } = e.target;
        // Prevent inner 'input' event from firing
        e.stopPropagation();
        e.preventDefault();
        this.setValue(value);
        this.filterOptionsDebounced();
        // Toggle menu based on value
        this.toggleMenuPane(!!this.value);
    }
    render() {
        return (h(Host, { key: 'bb3ee7f29195509439326e3611ee3075b9064ed3', tabIndex: 0, open: this.open }, h(HiddenHTMLDataList, { key: '89dd42da667b66d663f65f102de9f1cf64ee4598', id: this.name, options: this.optionsResolved, value: this.value }), h("wui-input", { key: 'a00e998b8944e23291eebf11549c63fc70472219', type: "search", list: this.name, value: this.value, placeholder: this.placeholder, loading: this.loading, name: this.name, disabled: this.disabled, error: this.error, canClear: this.canClear, ref: (elm) => {
                this.wuiInputElm = elm;
            }, onInput: (e) => {
                this.handleQuery(e);
            }, onKeyDown: (e) => {
                this.handleKeyDown(e);
            } }), h("wui-menu-pane", { key: 'dc21ded87d7be2fffaa18351e197b14feaf1c2b1', open: this.open, stretch: "min", placement: "bottom", onClickOutside: () => {
                this.toggleMenuPane(false);
            }, onOpenChanged: (e) => {
                this.toggleMenuPane(e.detail);
            } }, h("wui-list", { key: '9fb5c51b37d857b421e3a7c26f68231ae4114a62', scrollSize: "md" }, this.optionsResolved?.length ? (this.optionsResolved.map((option, idx) => {
            if (isOtherOption(option)) {
                switch (option.type) {
                    case 'header':
                        return h("wui-list-item-header", { key: idx, ...option });
                    case 'divider':
                        return h("wui-list-item-divider", { key: idx });
                    case 'meta':
                        return h("wui-list-item-meta", { key: idx, ...option });
                }
            }
            return (h("wui-list-item", {
                // ❓ We shouldn't use an array index for unique key, but
                // for some reason, if we use the unique value of `option`,
                // the whole rendering gets messed up during list keyboard
                // navigation. No idea what's going on there...
                key: idx, ...(typeof option === 'object' ? option : {}), label: `${toLabel(option)}`, disabled: !this.open || (typeof option === 'object' ? option.disabled : false), onClick: () => this.handleSelect(option)
            }));
        })) : (h("wui-list-item-meta", { label: this.noResultsMessage })), (() => {
            if (!this.suggestionsAction) {
                return;
            }
            if (!this.value) {
                return;
            }
            const suggestionMatchesValue = this.optionsResolved?.some((s) => !isOtherOption(s) && toValue(s) === this.value);
            if (suggestionMatchesValue) {
                return;
            }
            return (h(Fragment, null, h("wui-list-item-divider", null), h("wui-list-item", { variant: "button", label: [
                    this.suggestionsAction.action,
                    ' "',
                    [this.value, 'bold'],
                    '"',
                    this.suggestionsAction.type ? ` ${this.suggestionsAction.type}` : '',
                ], icon: this.suggestionsAction.icon ?? 'plus', onClick: () => {
                    this.toggleMenuPane(false);
                    if (!this.value) {
                        return;
                    }
                    this.suggestionsAction?.onSelect(this.value);
                } })));
        })()))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "options": ["watchOptions"]
    }; }
    static get style() { return autocompleteCss; }
}, [257, "wui-autocomplete", {
        "value": [1025],
        "name": [1],
        "options": [16],
        "placeholder": [1],
        "disabled": [1540],
        "loading": [1540],
        "canClear": [4, "can-clear"],
        "error": [520],
        "noResultsMessage": [1, "no-results-message"],
        "suggestionsAction": [16, "suggestions-action"],
        "open": [32],
        "searchQuery": [32],
        "optionsResolved": [32]
    }, undefined, {
        "options": ["watchOptions"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-autocomplete", "wui-box", "wui-check-input", "wui-close-button", "wui-divider", "wui-form-field", "wui-icon", "wui-input", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-autocomplete":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiAutocomplete$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiAutocomplete = WuiAutocomplete$1;
const defineCustomElement = defineCustomElement$1;

export { WuiAutocomplete, defineCustomElement };
